import axios from 'data/axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMapGL, { GeolocateControl, Marker, NavigationControl, Popup } from 'react-map-gl';

import GoogleAutocomplete from '../inputs/GoogleAutocomplete';
import { initialViewport, mapStyles } from './settings';

function MapLocationPicker({ popupLabel, coordinates: receivedCoordinates, onCoordinatesChange, initialCoordinates }) {
    const mapRef = useRef();
    const [viewport, setViewport] = useState(initialViewport);
    const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (initialCoordinates) {
            setCoordinates(initialCoordinates);
        }
    }, []);

    useEffect(() => {
        onCoordinatesChange(coordinates);
    }, [coordinates]);

    useEffect(() => {
        if (!receivedCoordinates) return;
        const { lat, lng } = receivedCoordinates;
        if (lat && lng) {
            setCoordinates({ lat, lng });
            setViewport({
                ...viewport,
                latitude: lat,
                longitude: lng,
                zoom: 13.5
            });
        }
    }, [receivedCoordinates]);

    const handleMapClick = useCallback((e) => {
        setCoordinates({
            lat: e.lngLat[1],
            lng: e.lngLat[0]
        });
    }, []);

    const handleMarkerDragEnd = useCallback((e) => {
        setCoordinates({
            lat: e.lngLat[1],
            lng: e.lngLat[0]
        });
        setIsDragging(false);
    }, []);

    const handleDragStart = useCallback(() => {
        setIsDragging(true);
    }, []);

    const handleViewportChange = useCallback((newViewport) => {
        setViewport({ ...newViewport });
    }, []);

    const handleGooglePlaceSelected = useCallback((place) => {
        if (!place) return null;

        axios
            .get(`/public/geocode?place_id=${place.place_id}`)
            .then(({ data }) => {
                const placeCoordinates = data.data[0]?.geometry?.location;
                if (placeCoordinates) {
                    setCoordinates(placeCoordinates);
                    setViewport({
                        ...viewport,
                        latitude: placeCoordinates.lat,
                        longitude: placeCoordinates.lng,
                        zoom: 13.5
                    });
                }
            })
            .catch((err) => {
                throw new Error(err);
            });
    }, []);

    return (
        <div className="w-full h-full max-w-full overflow-hidden relative">
            <div className="absolute top-2 left-2 z-10 w-48 sm:w-64">
                <GoogleAutocomplete onPlaceSelected={handleGooglePlaceSelected} />
            </div>

            <ReactMapGL
                {...viewport}
                width="100%"
                height="100%"
                mapStyle={mapStyles[0].url}
                maxZoom={20}
                onClick={handleMapClick}
                mapboxApiAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
                onViewportChange={handleViewportChange}
                attributionControl={false}
                touchRotate={false}
                ref={mapRef}
                disableTokenWarning={typeof window !== 'undefined' && window.location.hostname !== 'localhost'}
            >
                <NavigationControl style={{ right: 15, top: 15 }} />
                <GeolocateControl
                    style={{ right: 55, top: 15 }}
                    positionOptions={{ enableHighAccuracy: true }}
                    trackUserLocation
                />
                {coordinates.lat && coordinates.lng && (
                    <>
                        <Marker
                            latitude={coordinates.lat}
                            longitude={coordinates.lng}
                            offsetLeft={-14}
                            offsetTop={-38}
                            draggable
                            onDragEnd={handleMarkerDragEnd}
                            onDragStart={handleDragStart}
                        >
                            <img src="/images/maps/marker.png" alt="" className="pointer-events-none" />
                        </Marker>
                        {popupLabel && !isDragging && (
                            <Popup
                                latitude={coordinates.lat}
                                longitude={coordinates.lng}
                                closeButton={false}
                                closeOnClick={false}
                                offsetTop={-38}
                                offsetLeft={-1}
                                anchor="bottom"
                            >
                                <div className="bg-white p-2 text-sm">{popupLabel}</div>
                            </Popup>
                        )}
                    </>
                )}
            </ReactMapGL>
        </div>
    );
}

export default MapLocationPicker;
